import React, {useContext, useEffect, useState} from "react";
import {
    Avatar,
    Badge,
    Box,
    CardContent,
    CircularProgress,
    Divider,
    IconButton,
    List,
    ListItem,
    ListItemText,
    Skeleton,
    Stack,
    Tab,
    Tabs,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import {deleteComment, getComments, postComment, updateComment, getAllBalances} from "../../http/userAPI";
import {Context} from "../..";
import AppointmentRequestsList from "./AppointmentRequestsList";
import PaperRequestsList from "./PaperRequestsList";
import CertificateRequestsList from "./CertificateRequestsList";
import dayjs from "dayjs";
import ru from "dayjs/locale/ru";

dayjs.locale(ru);

const PatientCard = ({
                         selectedPatient,
                         changingChat,
                         loadAllRequests,
                         activeAppointments,
                         activeCertificates,
                         activePapers,
                         loadingRequests,
                         paperRequests,
                         appointmentRequests,
                         certificateRequests,

                         markAppointmentRequest,
                         markCertificateRequest,
                         markPaperRequest,
                     }) => {
    const {user} = useContext(Context);
    const [currentTab, setCurrentTab] = useState(0);
    const [balances, setBalances] = useState(null);

    // Comments state
    const [comments, setComments] = useState([]);
    const [loadingComments, setLoadingComments] = useState(false);
    const [adding, setAdding] = useState(false);
    const [newComment, setNewComment] = useState("");
    const [editingId, setEditingId] = useState(null);
    const [editingText, setEditingText] = useState("");
    const [updatingId, setUpdatingId] = useState(null);
    const [deletingId, setDeletingId] = useState(null);

    const formatDateTime = (val) => {
        if (!val) return "";
        try {
            return new Date(val).toLocaleString();
        } catch {
            return String(val);
        }
    };

    const formatShort = (val) => {
        if (!val) return "";
        try {
            const d = new Date(val);
            return d.toLocaleString([], {dateStyle: 'short', timeStyle: 'short'});
        } catch {
            return String(val);
        }
    };

    // Load comments
    useEffect(() => {
        let ignore = false;
        const load = async () => {
            if (!selectedPatient?.id) return;
            setLoadingComments(true);
            try {
                const data = await getComments(selectedPatient.id);
                const list = Array.isArray(data) ? data : (data?.comments || []);
                if (!ignore) setComments(list);
            } catch (e) {
                if (!ignore) setComments([]);
            } finally {
                if (!ignore) setLoadingComments(false);
            }
        };
        load();
        return () => {
            ignore = true;
        };
    }, [selectedPatient?.id]);

    // Load balances
    useEffect(() => {
        let ignore = false;
        const load = async () => {
            if (!selectedPatient?.id || !selectedPatient?.isParent) {
                setBalances(null);
                return;
            }
            try {
                const data = await getAllBalances(selectedPatient.id);
                if (!ignore) setBalances(data);
            } catch (e) {
                if (!ignore) setBalances(null);
            }
        };
        load();
        return () => {
            ignore = true;
        };
    }, [selectedPatient?.id, selectedPatient?.isParent]);


    useEffect(() => {
        if (selectedPatient?.id) {
            loadAllRequests(selectedPatient.id);
        }

    }, [selectedPatient?.id]);

    const handleAdd = async () => {
        if (!newComment.trim() || !selectedPatient?.id) return;
        setAdding(true);
        try {
            const created = await postComment(selectedPatient.id, newComment.trim());
            const item = Array.isArray(created) ? created[0] : (created?.comment || created);
            setComments((prev) => [item, ...prev]);
            setNewComment("");
        } catch (e) {
            alert(e?.message || "Не удалось добавить комментарий");
        } finally {
            setAdding(false);
        }
    };

    const startEdit = (c) => {
        setEditingId(c.id);
        setEditingText(c.value || "");
    };

    const cancelEdit = () => {
        setEditingId(null);
        setEditingText("");
    };

    const saveEdit = async (c) => {
        if (!editingText.trim()) {
            alert('Поле не может быть пустым');
            return;
        }
        if (!selectedPatient?.id || !c?.id || !editingText.trim()) return;
        setUpdatingId(c.id);
        try {
            const updated = await updateComment(c.id, editingText.trim());
            const item = Array.isArray(updated) ? updated[0] : (updated?.comment || updated);
            setComments((prev) => prev.map((it) => it.id === c.id ? {...it, ...item} : it));
            cancelEdit();
        } catch (e) {
            alert(e?.message || "Не удалось обновить комментарий");
        } finally {
            setUpdatingId(null);
        }
    };

    const removeComment = async (c) => {
        if (!selectedPatient?.id || !c?.id) return;
        if (!window.confirm("Удалить комментарий?")) return;
        setDeletingId(c.id);
        try {
            await deleteComment(c.id);
            setComments((prev) => prev.filter((it) => it.id !== c.id));
        } catch (e) {
            alert(e?.message || "Не удалось удалить комментарий");
        } finally {
            setDeletingId(null);
        }
    };


    if (changingChat || !selectedPatient) {
        return (
            <Box
                sx={{
                    borderRadius: 2,
                    border: "1px solid #e0e0e0",
                    width: "100%",
                    maxWidth: "600px",
                    minWidth: "400px",
                    height: "auto",
                    p: 2,
                    ml: 3,
                }}
            >
                <Box sx={{display: "flex", alignItems: "center", mb: 2}}>
                    <Skeleton variant="circular" width={64} height={64} sx={{mr: 2}}/>
                    <Box>
                        <Skeleton variant="text" width={120} height={30}/>
                        <Skeleton variant="text" width={80} height={20}/>
                    </Box>
                </Box>

                <CardContent sx={{pt: 0}}>
                    <Skeleton variant="text" width="80%"/>
                    <Skeleton variant="text" width="60%"/>
                    <Skeleton variant="text" width="50%" sx={{mt: 1}}/>
                    <Divider sx={{my: 2}}/>
                    <Skeleton variant="text" width="40%"/>
                    <List dense>
                        <ListItem>
                            <Skeleton variant="text" width="100%"/>
                        </ListItem>
                        <ListItem>
                            <Skeleton variant="text" width="100%"/>
                        </ListItem>
                    </List>
                </CardContent>
            </Box>
        );
    }

    return (
        <Box
            sx={{
                borderRadius: 2,
                border: "1px solid #e0e0e0",
                width: "100%",
                maxWidth: "600px",
                minWidth: "400px",
                height: "auto",
                maxHeight: "100%",
                p: 2,
                ml: 3,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
            }}
        >
            <Box sx={{display: "flex", alignItems: "center", mb: 2}}>
                <Avatar sx={{width: 64, height: 64, mr: 2}}>
                    {selectedPatient?.name ? selectedPatient?.name[0].toUpperCase() : "?"}
                </Avatar>
                <Box>
                    <Typography variant="h6">{selectedPatient?.name || "Неизвестно"}</Typography>
                    <Typography variant="body2" color="text.secondary">
                        {selectedPatient.patientClinicId ? `ID: ${selectedPatient.patientClinicId}` : 'Не сопоставлен'}
                    </Typography>
                </Box>
            </Box>

            <CardContent sx={{pt: 0, display: 'flex', flexDirection: 'column', flex: 1, overflow: 'auto', pr: 1}}>
                <Typography variant="body2">
                    <strong>Телефон:</strong> {selectedPatient?.phone || "—"}
                </Typography>
                <Typography variant="body2">
                    <strong>Бот заблокирован:</strong> {selectedPatient?.isBotBlocked ? "Да" : "Нет"}
                </Typography>
                <Typography variant="body2">
                    <strong>Дата
                        рождения:</strong> {selectedPatient?.birthday ? dayjs(selectedPatient?.birthday).format('DD MMMM YYYY') : "—"}
                </Typography>
                <Typography variant="body2">
                    <strong>Филиал:</strong> {selectedPatient?.branch?.name || "—"}
                </Typography>
                <Typography variant="body2" sx={{mt: 1}}>
                    <strong>Баллов:</strong> {selectedPatient?.points ?? 0} ₽
                </Typography>

                {balances && balances.children && balances.children.length > 0 && (
                    <>
                        <Divider sx={{my: 1.5}}/>
                        <Typography variant="subtitle2" sx={{fontWeight: 'bold', mb: 0.5}}>
                            Балансы детей:
                        </Typography>
                        <List dense disablePadding>
                            {balances.children.map((child) => (
                                <ListItem key={child.id} sx={{px: 0, py: 0.25}}>
                                    <ListItemText
                                        primary={`${child.name || 'Без имени'}: ${child.balance} ₽`}
                                        primaryTypographyProps={{variant: 'body2'}}
                                    />
                                </ListItem>
                            ))}
                        </List>
                    </>
                )}

                {selectedPatient?.pets?.length > 0 && (
                    <>
                        <Divider sx={{my: 2}}/>
                        <Typography variant="subtitle1">Питомцы:</Typography>
                        <List dense>
                            {selectedPatient?.pets.map((pet, index) => (
                                <ListItem key={index}>
                                    <ListItemText primary={pet.name}/>
                                </ListItem>
                            ))}
                        </List>
                    </>
                )}

                <Divider sx={{my: 2}}/>

                {/* Tabs with badges */}
                <Tabs
                    value={currentTab}
                    onChange={(e, v) => setCurrentTab(v)}
                    sx={{
                        minHeight: 36,
                        '& .MuiTabs-flexContainer': {
                            gap: 0.5
                        }
                    }}
                    variant="scrollable"
                    scrollButtons={false}
                >
                    <Tab
                        label="Комментарии"
                        sx={{
                            minHeight: 36,
                            py: 0.5,
                            px: 1.5,
                            minWidth: 'auto',
                            fontSize: '0.875rem'
                        }}
                    />
                    <Tab
                        label={
                            <Badge
                                badgeContent={activeAppointments}
                                color="error"
                                sx={{
                                    '& .MuiBadge-badge': {
                                        transform: 'translate(8px, -8px)',
                                        fontSize: '0.7rem',
                                        height: 16,
                                        minWidth: 16
                                    }
                                }}
                            >
                                <Box component="span" sx={{mr: 0.5}}>Записи</Box>
                            </Badge>
                        }
                        sx={{
                            minHeight: 36,
                            py: 0.5,
                            px: 1.5,
                            minWidth: 'auto',
                            fontSize: '0.875rem'
                        }}
                    />
                    <Tab
                        label={
                            <Badge
                                badgeContent={activePapers}
                                color="error"
                                sx={{
                                    '& .MuiBadge-badge': {
                                        transform: 'translate(8px, -8px)',
                                        fontSize: '0.7rem',
                                        height: 16,
                                        minWidth: 16
                                    }
                                }}
                            >
                                <Box component="span" sx={{mr: 0.5}}>Справки</Box>
                            </Badge>
                        }
                        sx={{
                            minHeight: 36,
                            py: 0.5,
                            px: 1.5,
                            minWidth: 'auto',
                            fontSize: '0.875rem'
                        }}
                    />
                    <Tab
                        label={
                            <Badge
                                badgeContent={activeCertificates}
                                color="error"
                                sx={{
                                    '& .MuiBadge-badge': {
                                        transform: 'translate(8px, -8px)',
                                        fontSize: '0.7rem',
                                        height: 16,
                                        minWidth: 16
                                    }
                                }}
                            >
                                <Box component="span" sx={{mr: 0.5}}>Сертификаты</Box>
                            </Badge>
                        }
                        sx={{
                            minHeight: 36,
                            py: 0.5,
                            px: 1.5,
                            minWidth: 'auto',
                            fontSize: '0.875rem'
                        }}
                    />
                </Tabs>

                {/* Tab content */}
                <Box sx={{mt: 1}}>
                    {currentTab === 0 && (
                        <>
                            <Stack direction="row" spacing={1} alignItems="center">
                                <TextField
                                    size="small"
                                    fullWidth
                                    placeholder="Комментарий"
                                    value={newComment}
                                    onChange={(e) => setNewComment(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' && !e.shiftKey) {
                                            e.preventDefault();
                                            handleAdd();
                                        }
                                    }}
                                />
                                <Tooltip title="Добавить">
                                    <span>
                                        <IconButton color="primary" onClick={handleAdd}
                                                    disabled={!newComment.trim() || adding}
                                                    size="small">
                                            {adding ? <CircularProgress size={18} thickness={6}/> : <AddIcon/>}
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            </Stack>

                            {loadingComments ? (
                                <List dense sx={{mt: 1}}>
                                    {[...Array(3)].map((_, i) => (
                                        <ListItem key={i}>
                                            <ListItemText
                                                primary={<Skeleton variant="text" width="90%"/>}
                                                secondary={<Skeleton variant="text" width="50%"/>}
                                            />
                                        </ListItem>
                                    ))}
                                </List>
                            ) : (
                                <List dense sx={{mt: 1}}>
                                    {comments.length === 0 && (
                                        <Typography variant="body2" color="text.secondary" sx={{px: 2, py: 1}}>
                                            Нет комментариев
                                        </Typography>
                                    )}
                                    {comments.map((c) => (
                                        <ListItem
                                            key={c.id}
                                            alignItems="flex-start"
                                            secondaryAction={
                                                <Stack direction="row" spacing={1}>
                                                    {editingId === c.id ? (
                                                        <>
                                                            <Tooltip title="Сохранить">
                                                                <span>
                                                                    <IconButton
                                                                        edge="end"
                                                                        onClick={() => saveEdit(c)}
                                                                        disabled={updatingId === c.id}
                                                                        size="small"
                                                                    >
                                                                        {updatingId === c.id ? (
                                                                            <CircularProgress size={16} thickness={6}/>
                                                                        ) : (
                                                                            <SaveIcon fontSize="small"/>
                                                                        )}
                                                                    </IconButton>
                                                                </span>
                                                            </Tooltip>
                                                            <Tooltip title="Отмена">
                                                                <IconButton edge="end" onClick={cancelEdit}
                                                                            size="small">
                                                                    <CloseIcon fontSize="small"/>
                                                                </IconButton>
                                                            </Tooltip>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <Tooltip title="Редактировать">
                                                                <IconButton edge="end" onClick={() => startEdit(c)}
                                                                            size="small">
                                                                    <EditIcon fontSize="small"/>
                                                                </IconButton>
                                                            </Tooltip>
                                                            <Tooltip title="Удалить">
                                                                <span>
                                                                    <IconButton
                                                                        edge="end"
                                                                        onClick={() => removeComment(c)}
                                                                        disabled={deletingId === c.id}
                                                                        size="small"
                                                                    >
                                                                        {deletingId === c.id ? (
                                                                            <CircularProgress size={16} thickness={6}/>
                                                                        ) : (
                                                                            <DeleteIcon fontSize="small"/>
                                                                        )}
                                                                    </IconButton>
                                                                </span>
                                                            </Tooltip>
                                                        </>
                                                    )}
                                                </Stack>
                                            }
                                        >
                                            {editingId === c.id ? (
                                                <TextField
                                                    fullWidth
                                                    size="small"
                                                    value={editingText}
                                                    onChange={(e) => setEditingText(e.target.value)}
                                                    multiline
                                                />
                                            ) : (
                                                <ListItemText
                                                    primaryTypographyProps={{variant: 'body2'}}
                                                    primary={
                                                        <>
                                                            <span>{c.value || ""}</span>
                                                            {c.lastUpdater && (
                                                                <Typography component="span" variant="caption"
                                                                            color="text.secondary" sx={{ml: 0.5}}>
                                                                    (изм)
                                                                </Typography>
                                                            )}
                                                        </>
                                                    }
                                                    secondary={
                                                        <Typography variant="caption" color="text.secondary">
                                                            {c.createdAt && !c.lastUpdater ? `${c.author?.id === user.user.id ? 'Вы' : c.author?.name} • ${formatShort(c.createdAt)}` : ''}
                                                            {c.updatedAt && c.lastUpdater ? `${c.lastUpdater?.id === user.user.id ? 'Вы' : c.lastUpdater?.name} • ${formatShort(c.updatedAt)}` : ''}
                                                        </Typography>
                                                    }
                                                />
                                            )}
                                        </ListItem>
                                    ))}
                                </List>
                            )}
                        </>
                    )}

                    {currentTab === 1 && (
                        <>
                            {loadingRequests ? (
                                <CircularProgress size={24} sx={{display: 'block', mx: 'auto', mt: 2}}/>
                            ) : (
                                <AppointmentRequestsList
                                    requests={appointmentRequests}
                                    markHandler={markAppointmentRequest}
                                />
                            )}
                        </>
                    )}

                    {currentTab === 2 && (
                        <>
                            {loadingRequests ? (
                                <CircularProgress size={24} sx={{display: 'block', mx: 'auto', mt: 2}}/>
                            ) : (
                                <PaperRequestsList
                                    requests={paperRequests}
                                    markHandler={markPaperRequest}
                                />
                            )}
                        </>
                    )}

                    {currentTab === 3 && (
                        <>
                            {loadingRequests ? (
                                <CircularProgress size={24} sx={{display: 'block', mx: 'auto', mt: 2}}/>
                            ) : (
                                <CertificateRequestsList
                                    requests={certificateRequests}
                                    markHandler={markCertificateRequest}
                                />
                            )}
                        </>
                    )}
                </Box>
            </CardContent>
        </Box>
    );
};

export default PatientCard;
